import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { ShieldCheckIcon } from '../../assets/icons';
import { useOnboardingFooter } from '../../components/OnboardingFooterContext';
import * as userActions from '../../store/actions/user';
import * as configActions from '../../store/actions/config';
import { UserType } from '../../enums';
import { isKycOrLaterOnboardingStep, getResolvedNextStepPath, getResolvedPreviousStepPath } from '../../utils/onboardingUtils';
import { buildUserOnboardingProfilePayload, normalizeMultiValue } from '../../utils/financialOnboardingStorage';
import { requiresEnhancedIndividualKyc } from '../../utils/individualEnhancedKyc';
import normalizeReferenceCountries from '../../utils/referenceCountries';
import SearchableSelect from '../../components/SearchableSelect';
import CompanyRisk from '../CompanyRisk';

const fieldLabelClass = 'mb-2 block text-[12px] font-semibold text-[#3D5A73]';
const errorClass = 'mt-1.5 text-xs font-medium text-[#C0392B]';
const inputClass =
  'h-11 w-full rounded-[10px] border border-[#DDE6EF] bg-white px-3.5 text-sm text-[#0B1929] outline-none transition placeholder:text-[#7A9AB0]/70 focus:border-[#2E6BAD] focus:ring-4 focus:ring-[#2E6BAD]/10';
const searchableSelectTriggerClass =
  'flex h-11 w-full items-center gap-2 rounded-[10px] border border-[#DDE6EF] bg-white px-3.5 text-sm text-[#0B1929] outline-none transition focus:border-[#2E6BAD] focus:ring-4 focus:ring-[#2E6BAD]/10 disabled:cursor-not-allowed disabled:opacity-50';
const textareaClass =
  'min-h-[92px] w-full rounded-[10px] border border-[#DDE6EF] bg-white px-3.5 py-3 text-sm text-[#0B1929] outline-none transition placeholder:text-[#7A9AB0]/70 focus:border-[#2E6BAD] focus:ring-4 focus:ring-[#2E6BAD]/10';
const explanationClass = 'mt-3 rounded-[12px] bg-[#F4F8FC] px-4 py-3 text-[13px] leading-6 text-[#4E6F8A]';

function booleanFromStored(value, defaultValue = null) {
  if (typeof value === 'boolean') return value;
  if (value === 'true') return true;
  if (value === 'false') return false;
  return defaultValue;
}

function createCitizenshipRow(value = '') {
  return {
    id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
    value,
  };
}

function InfoTooltip({ text }) {
  return (
    <span className='group relative ml-1.5 inline-flex align-middle'>
      <button
        type='button'
        className='inline-flex h-[17px] w-[17px] cursor-help items-center justify-center rounded-full border border-[#B8C9D9] bg-[#F4F8FC] text-[11px] font-bold leading-none text-[#3D5A73] outline-none transition hover:border-[#1E4E80] hover:text-[#1E4E80] focus:border-[#1E4E80] focus:text-[#1E4E80] focus:ring-2 focus:ring-[#2E6BAD]/10'
      >
        i
      </button>
      <span className='pointer-events-none absolute right-0 top-full z-20 mt-2 hidden w-[min(280px,calc(100vw-48px))] rounded-[10px] bg-[#0B1929] px-3.5 py-3 text-left text-[12px] font-medium leading-5 text-white shadow-[0_12px_30px_rgba(11,25,41,0.18)] group-hover:block group-focus-within:block sm:left-1/2 sm:right-auto sm:w-[280px] sm:-translate-x-1/2'>
        {text}
      </span>
    </span>
  );
}

InfoTooltip.propTypes = {
  text: PropTypes.string.isRequired,
};

function ComplianceIndividual() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const setFooterConfig = useOnboardingFooter();
  const { user, onboardingStatus, upsertingUserOnboardingProfile, upsertUserOnboardingProfileError } = useSelector((state) => state.user);
  const referenceCountries = useSelector((state) => state.config?.referenceCountries ?? []);
  const referenceCountriesLoading = useSelector((state) => state.config?.referenceCountriesLoading ?? false);
  const referenceCountriesError = useSelector((state) => state.config?.referenceCountriesError);
  const existingProfile = useMemo(
    () => user?.userDetails?.onboardingProfile ?? user?.userDetails?.OnboardingProfile ?? {},
    [user?.userDetails?.OnboardingProfile, user?.userDetails?.onboardingProfile],
  );
  const isEnhancedKyc = useMemo(() => requiresEnhancedIndividualKyc(user), [user]);
  const showFinancialCrimeQuestion = isEnhancedKyc;
  const [pep, setPep] = useState(null);
  const [pepPosition, setPepPosition] = useState('');
  const [pepInstitution, setPepInstitution] = useState('');
  const [pepCountry, setPepCountry] = useState('');
  const [pepDates, setPepDates] = useState('');
  const [sanctioned, setSanctioned] = useState(null);
  const [sanctionsDetail, setSanctionsDetail] = useState('');
  const [criminalConvictions, setCriminalConvictions] = useState(null);
  const [criminalDetail, setCriminalDetail] = useState('');
  const [hasOtherCitizenships, setHasOtherCitizenships] = useState(null);
  const [citizenships, setCitizenships] = useState([createCitizenshipRow()]);
  const [errors, setErrors] = useState({});
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [pendingBackendProfile, setPendingBackendProfile] = useState(null);
  const [hasSeenBackendSaving, setHasSeenBackendSaving] = useState(false);

  const globalCountryOptions = useMemo(() => normalizeReferenceCountries(referenceCountries), [referenceCountries]);

  const countrySelectOptions = useMemo(
    () =>
      globalCountryOptions.map((country) => ({
        value: country.code,
        label: country.name,
        searchText: `${country.name} ${country.code}`,
      })),
    [globalCountryOptions],
  );

  const nextStepPath =
    getResolvedNextStepPath('/compliance', onboardingStatus?.steps, { userType: user?.userType }) ?? '/accept-legal-agreements';
  const isKycOrLaterStep = isKycOrLaterOnboardingStep(onboardingStatus?.currentStep);

  useEffect(() => {
    if (globalCountryOptions.length === 0 && !referenceCountriesLoading && !referenceCountriesError) {
      dispatch(configActions.getReferenceCountries());
    }
  }, [dispatch, globalCountryOptions.length, referenceCountriesError, referenceCountriesLoading]);

  useEffect(() => {
    const profile = existingProfile;
    const pepAssociation = profile.pepAssociation ?? profile.PepAssociation ?? {};

    const pepStored = booleanFromStored(
      profile.isPoliticallyExposed ?? profile.IsPoliticallyExposed ?? profile.pep ?? profile.politicallyExposed ?? profile.PoliticallyExposed,
    );
    const sanctionedStored = booleanFromStored(profile.isSanctioned ?? profile.IsSanctioned ?? profile.sanctioned ?? profile.Sanctioned);
    const criminalStored = booleanFromStored(
      profile.hasCriminalConvictions ?? profile.HasCriminalConvictions ?? profile.criminalConvictions ?? profile.CriminalConvictions,
    );

    setPep(pepStored);
    setPepPosition(pepAssociation.role ?? profile.pepPosition ?? profile.pepRole ?? profile.PepRole ?? '');
    setPepInstitution(
      pepAssociation.institutionOrOrganisation ??
        profile.pepInstitution ??
        profile.pepInstitutionOrOrganisation ??
        profile.PepInstitutionOrOrganisation ??
        '',
    );
    setPepCountry(pepAssociation.country ?? profile.pepCountry ?? profile.PepCountry ?? '');
    setPepDates(pepAssociation.datesHeld ?? profile.pepDates ?? profile.PepDates ?? '');
    setSanctioned(sanctionedStored);
    setSanctionsDetail(profile.sanctionsDetail ?? profile.SanctionsDetail ?? '');
    setCriminalConvictions(criminalStored);
    setCriminalDetail(
      profile.criminalConvictionsDetail ?? profile.CriminalConvictionsDetail ?? profile.criminalDetail ?? profile.CriminalDetail ?? '',
    );

    const storedCitizenships = normalizeMultiValue(profile.otherCitizenships ?? profile.OtherCitizenships);
    const hasStoredOtherCitizenships = storedCitizenships.length >= 2;
    const profileAnswered =
      pepStored !== null || sanctionedStored !== null || criminalStored !== null || hasStoredOtherCitizenships;
    let otherCitizenshipsAnswer = null;
    if (hasStoredOtherCitizenships) otherCitizenshipsAnswer = true;
    else if (profileAnswered) otherCitizenshipsAnswer = false;
    setHasOtherCitizenships(otherCitizenshipsAnswer);
    setCitizenships(
      hasStoredOtherCitizenships
        ? storedCitizenships.map((value) => createCitizenshipRow(value))
        : [createCitizenshipRow(), createCitizenshipRow()],
    );
  }, [existingProfile]);

  const validate = useCallback(() => {
    const nextErrors = {};

    if (typeof pep !== 'boolean') nextErrors.pep = 'Please answer this question.';
    if (pep === true) {
      if (!pepPosition.trim()) nextErrors.pepPosition = 'Enter the position or role held.';
      if (!pepInstitution.trim()) nextErrors.pepInstitution = 'Enter the institution or organisation.';
      if (!pepCountry) nextErrors.pepCountry = 'Select the country.';
    }

    if (typeof sanctioned !== 'boolean') nextErrors.sanctioned = 'Please answer this question.';
    if (sanctioned === true && !sanctionsDetail.trim()) {
      nextErrors.sanctionsDetail = 'Please provide sanctions details.';
    }

    if (showFinancialCrimeQuestion) {
      if (typeof criminalConvictions !== 'boolean') nextErrors.criminalConvictions = 'Please answer this question.';
      if (criminalConvictions === true && !criminalDetail.trim()) {
        nextErrors.criminalDetail = 'Please provide conviction details.';
      }
    }

    if (typeof hasOtherCitizenships !== 'boolean') nextErrors.hasOtherCitizenships = 'Please answer this question.';
    const selectedCitizenships = citizenships.map((citizenship) => citizenship.value).filter(Boolean);
    const uniqueCitizenshipCount = new Set(selectedCitizenships).size;
    if (hasOtherCitizenships === true && uniqueCitizenshipCount < 2) {
      nextErrors.citizenships =
        selectedCitizenships.length >= 2 && uniqueCitizenshipCount < selectedCitizenships.length
          ? 'Each citizenship must be a different country.'
          : 'Select at least two different citizenships.';
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  }, [
    citizenships,
    criminalConvictions,
    criminalDetail,
    hasOtherCitizenships,
    pep,
    pepCountry,
    pepInstitution,
    pepPosition,
    sanctioned,
    sanctionsDetail,
    showFinancialCrimeQuestion,
  ]);

  useEffect(() => {
    if (hasSubmitted) {
      validate();
    }
  }, [
    citizenships,
    criminalConvictions,
    criminalDetail,
    hasOtherCitizenships,
    hasSubmitted,
    pep,
    pepCountry,
    pepInstitution,
    pepPosition,
    sanctioned,
    sanctionsDetail,
    validate,
  ]);

  useEffect(() => {
    if (hasOtherCitizenships === true && citizenships.length < 2) {
      setCitizenships((current) => [...current, ...Array.from({ length: 2 - current.length }, () => createCitizenshipRow())]);
    }
  }, [citizenships.length, hasOtherCitizenships]);

  const handleOtherCitizenshipsChange = (value) => {
    setHasOtherCitizenships(value);
    if (value !== true) {
      setErrors((current) => {
        const { citizenships: citizenshipError, ...rest } = current;
        return rest;
      });
    }
  };

  const updateCitizenship = (id, value) => {
    setCitizenships((current) => current.map((citizenship) => (citizenship.id === id ? { ...citizenship, value } : citizenship)));
  };

  const addCitizenship = () => {
    setCitizenships((current) => [...current, createCitizenshipRow()]);
  };

  const removeCitizenship = (id) => {
    setCitizenships((current) => {
      const next = current.filter((citizenship) => citizenship.id !== id);
      return next.length > 0 ? next : [createCitizenshipRow()];
    });
  };

  const handleContinue = useCallback(() => {
    if (isKycOrLaterStep) {
      navigate(nextStepPath);
      return;
    }

    setHasSubmitted(true);
    if (!validate()) return;

    const cleanCitizenships = [...new Set(citizenships.map((citizenship) => citizenship.value).filter(Boolean))];
    const profilePatch = {
      isPoliticallyExposed: pep,
      pepAssociation:
        pep === true
          ? {
              role: pepPosition.trim(),
              institutionOrOrganisation: pepInstitution.trim(),
              country: pepCountry,
              datesHeld: pepDates.trim() || null,
            }
          : null,
      isSanctioned: sanctioned,
      sanctionsDetail: sanctioned === true ? sanctionsDetail.trim() : null,
      hasCriminalConvictions: showFinancialCrimeQuestion ? criminalConvictions : null,
      criminalConvictionsDetail: showFinancialCrimeQuestion && criminalConvictions === true ? criminalDetail.trim() : null,
      otherCitizenships: hasOtherCitizenships === true ? cleanCitizenships.join(',') : null,
    };

    const profile = buildUserOnboardingProfilePayload({
      ...existingProfile,
      ...profilePatch,
    });
    setPendingBackendProfile(profile);
    setHasSeenBackendSaving(false);
    dispatch(userActions.upsertUserOnboardingProfile(profile));
  }, [
    citizenships,
    criminalConvictions,
    criminalDetail,
    dispatch,
    existingProfile,
    isKycOrLaterStep,
    navigate,
    pep,
    pepCountry,
    pepDates,
    pepInstitution,
    pepPosition,
    sanctioned,
    sanctionsDetail,
    hasOtherCitizenships,
    nextStepPath,
    showFinancialCrimeQuestion,
    validate,
  ]);

  const handleBack = useCallback(() => {
    const previousPath =
      getResolvedPreviousStepPath('/compliance', onboardingStatus?.steps, { userType: user?.userType }) ?? '/financial';
    navigate(previousPath);
  }, [navigate, onboardingStatus?.steps, user?.userType]);

  useEffect(() => {
    setFooterConfig({
      continueLabel: upsertingUserOnboardingProfile ? 'Saving...' : 'Continue',
      continueDisabled: upsertingUserOnboardingProfile,
      onContinue: handleContinue,
      backDisabled: upsertingUserOnboardingProfile,
      onBack: handleBack,
    });
  }, [handleBack, handleContinue, setFooterConfig, upsertingUserOnboardingProfile]);

  useEffect(() => () => setFooterConfig({}), [setFooterConfig]);

  useEffect(() => {
    if (pendingBackendProfile && upsertingUserOnboardingProfile) {
      setHasSeenBackendSaving(true);
    }
  }, [pendingBackendProfile, upsertingUserOnboardingProfile]);

  useEffect(() => {
    if (pendingBackendProfile && hasSeenBackendSaving && !upsertingUserOnboardingProfile && !upsertUserOnboardingProfileError) {
      setPendingBackendProfile(null);
      setHasSeenBackendSaving(false);
      navigate(nextStepPath);
    }
  }, [hasSeenBackendSaving, navigate, nextStepPath, pendingBackendProfile, upsertUserOnboardingProfileError, upsertingUserOnboardingProfile]);

  const renderBooleanCards = (value, onChange) => (
    <div className='mt-4 grid grid-cols-2 gap-2'>
      {[
        { value: false, label: 'No' },
        { value: true, label: 'Yes' },
      ].map((option) => {
        const isSelected = value === option.value;
        return (
          <button
            key={option.label}
            type='button'
            onClick={() => onChange(option.value)}
            className={`rounded-[12px] border bg-white px-4 py-3 text-left text-sm font-bold transition ${
              isSelected ? 'border-[#1E4E80] shadow-[0_0_0_3px_rgba(30,78,128,0.08)]' : 'border-[#DDE6EF] hover:border-[#B8C9D9]'
            }`}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );

  return (
    <div className='flex w-full justify-center bg-[#EBF4FB] px-4 pt-7 font-sans text-[#0B1929] sm:min-h-screen sm:px-6'>
      <div className='w-full max-w-[540px]'>
        <div className='rounded-[20px] bg-white px-6 py-8 shadow-[0_4px_32px_rgba(11,25,41,0.08)] sm:mb-24 sm:px-10 sm:py-9'>
          <fieldset disabled={isKycOrLaterStep} className='block border-0 p-0'>
            <div className='mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-full bg-[#EBF4FB] text-[#1E4E80]'>
              <ShieldCheckIcon className='h-6 w-6' />
            </div>

            <h1 className='mb-2.5 font-serif text-2xl font-normal leading-[1.25] text-[#0B1929]'>A few quick declarations.</h1>
            <p className='mt-2 text-sm leading-6 text-[#4E6F8A]'>Required by EU regulations.</p>

            <div className='mt-6 rounded-[16px] border border-[#DDE6EF] p-4'>
              <div className='px-1 text-[12px] font-semibold leading-5 text-[#3D5A73]'>
                Are you, or have you been in the last 12 months, a politically exposed person, or an immediate family member or close associate of one?
                <InfoTooltip text='A politically exposed person (PEP) holds or has held a senior public function - such as head of state, government minister, senior judge, military officer, or director of a state-owned enterprise. This includes their immediate family members and close associates.' />
              </div>
              {renderBooleanCards(pep, setPep)}
              {hasSubmitted && errors.pep && <p className={errorClass}>{errors.pep}</p>}
            {pep === true && (
              <>
                <div className={explanationClass}>
                  A politically exposed person (PEP) holds or has held a senior public function. This also includes immediate family members and close
                  associates.
                </div>
                <div className='mt-4 space-y-4'>
                  <div>
                    <label htmlFor='pepPosition' className={fieldLabelClass}>
                      Position or role held
                    </label>
                    <input id='pepPosition' type='text' value={pepPosition} onChange={(event) => setPepPosition(event.target.value)} className={inputClass} />
                    {errors.pepPosition && <p className={errorClass}>{errors.pepPosition}</p>}
                  </div>
                  <div>
                    <label htmlFor='pepInstitution' className={fieldLabelClass}>
                      Institution or organisation
                    </label>
                    <input
                      id='pepInstitution'
                      type='text'
                      value={pepInstitution}
                      onChange={(event) => setPepInstitution(event.target.value)}
                      className={inputClass}
                    />
                    {errors.pepInstitution && <p className={errorClass}>{errors.pepInstitution}</p>}
                  </div>
                  <div className='grid gap-4 sm:grid-cols-2'>
                    <div>
                      <label htmlFor='pepCountry' className={fieldLabelClass}>
                        Country
                      </label>
                      <SearchableSelect
                        id='pepCountry'
                        name='pepCountry'
                        value={pepCountry}
                        onChange={(event) => setPepCountry(event.target.value)}
                        placeholder={referenceCountriesLoading ? 'Loading countries...' : 'Select...'}
                        options={countrySelectOptions}
                        showCountryFlags
                        searchPlaceholder='Search country...'
                        menuPlacement='auto'
                        menuMinWidth='w-full'
                        clearable
                        hasError={Boolean(errors.pepCountry)}
                        triggerClassName={searchableSelectTriggerClass}
                      />
                      {referenceCountriesError && (
                        <p className={errorClass}>Could not load countries. Please refresh and try again.</p>
                      )}
                      {errors.pepCountry && <p className={errorClass}>{errors.pepCountry}</p>}
                    </div>
                    <div>
                      <label htmlFor='pepDates' className={fieldLabelClass}>
                        Dates held <span className='font-normal normal-case opacity-60'>(optional)</span>
                      </label>
                      <input
                        id='pepDates'
                        type='text'
                        value={pepDates}
                        onChange={(event) => setPepDates(event.target.value)}
                        placeholder='e.g. 2018-2023'
                        className={inputClass}
                      />
                    </div>
                  </div>
                </div>
              </>
            )}
            </div>

            <div className='mt-5 rounded-[16px] border border-[#DDE6EF] p-4'>
              <div className='px-1 text-[12px] font-semibold leading-5 text-[#3D5A73]'>
                Are you subject to any international sanctions?
                <InfoTooltip text='International sanctions are restrictions imposed by the EU, UN, US (OFAC), or UK. Examples include asset freezes or travel bans linked to terrorism, proliferation, or serious human rights violations.' />
              </div>
              {renderBooleanCards(sanctioned, setSanctioned)}
              {hasSubmitted && errors.sanctioned && <p className={errorClass}>{errors.sanctioned}</p>}
            {sanctioned === true && (
              <>
                <div className={explanationClass}>
                  International sanctions are legal restrictions imposed by the EU, UN, United States (OFAC), or United Kingdom.
                </div>
                <div className='mt-4'>
                  <label htmlFor='sanctionsDetail' className={fieldLabelClass}>
                    Please provide details
                  </label>
                  <textarea
                    id='sanctionsDetail'
                    value={sanctionsDetail}
                    onChange={(event) => setSanctionsDetail(event.target.value)}
                    placeholder='e.g. which sanctions list, country, date'
                    className={textareaClass}
                  />
                  {errors.sanctionsDetail && <p className={errorClass}>{errors.sanctionsDetail}</p>}
                </div>
              </>
            )}
            </div>

            {showFinancialCrimeQuestion ? (
            <div className='mt-5 rounded-[16px] border border-[#DDE6EF] p-4'>
              <div className='px-1 text-[12px] font-semibold leading-5 text-[#3D5A73]'>
                Have you ever been convicted of a serious financial crime?
                <InfoTooltip text='This includes fraud, money laundering, tax evasion, insider trading, or similar offences. We are required to ask this to meet our legal obligations.' />
              </div>
              {renderBooleanCards(criminalConvictions, setCriminalConvictions)}
              {hasSubmitted && errors.criminalConvictions && <p className={errorClass}>{errors.criminalConvictions}</p>}
            {criminalConvictions === true && (
              <>
                <div className={explanationClass}>
                  This includes fraud, money laundering, tax evasion, insider trading, or similar offences.
                </div>
                <div className='mt-4'>
                  <label htmlFor='criminalDetail' className={fieldLabelClass}>
                    Please provide details
                  </label>
                  <textarea
                    id='criminalDetail'
                    value={criminalDetail}
                    onChange={(event) => setCriminalDetail(event.target.value)}
                    placeholder='e.g. nature of offence, jurisdiction, date'
                    className={textareaClass}
                  />
                  {errors.criminalDetail && <p className={errorClass}>{errors.criminalDetail}</p>}
                </div>
              </>
            )}
            </div>
            ) : null}

            <div className='mt-5 rounded-[16px] border border-[#DDE6EF] p-4'>
              <div className='px-1 text-[12px] font-semibold leading-5 text-[#3D5A73]'>
                Do you hold citizenship in more than one country?
              </div>
              {renderBooleanCards(hasOtherCitizenships, handleOtherCitizenshipsChange)}
              {hasSubmitted && errors.hasOtherCitizenships && <p className={errorClass}>{errors.hasOtherCitizenships}</p>}
            </div>

            {hasOtherCitizenships === true && (
              <div className='mt-5 rounded-[16px] border border-[#DDE6EF] p-4'>
              <div className={fieldLabelClass}>Please list all citizenships you hold</div>
              <p className='mb-3 px-1 text-[12px] leading-5 text-[#4E6F8A]'>Add at least two different citizenships.</p>
              <div className='space-y-2'>
                {citizenships.map((citizenship) => {
                  const selectedInOtherRows = citizenships
                    .filter((row) => row.id !== citizenship.id)
                    .map((row) => row.value)
                    .filter(Boolean);
                  const availableCountryOptions = globalCountryOptions.filter(
                    (country) => country.code === citizenship.value || !selectedInOtherRows.includes(country.code),
                  );

                  const availableCountrySelectOptions = availableCountryOptions.map((country) => ({
                    value: country.code,
                    label: country.name,
                    searchText: `${country.name} ${country.code}`,
                  }));

                  return (
                  <div key={citizenship.id} className='flex w-full flex-col gap-2 sm:flex-row'>
                    <SearchableSelect
                      id={`citizenship-${citizenship.id}`}
                      name={`citizenship-${citizenship.id}`}
                      value={citizenship.value}
                      onChange={(event) => updateCitizenship(citizenship.id, event.target.value)}
                      placeholder={referenceCountriesLoading ? 'Loading countries...' : 'Select country...'}
                      options={availableCountrySelectOptions}
                      showCountryFlags
                      searchPlaceholder='Search country...'
                      menuPlacement='auto'
                      menuMinWidth='w-full'
                      clearable
                      className='min-w-0 flex-1'
                      triggerClassName={searchableSelectTriggerClass}
                    />
                    {citizenships.length > 1 && (
                      <button
                        type='button'
                        onClick={() => removeCitizenship(citizenship.id)}
                        className='rounded-[10px] border border-[#DDE6EF] px-3 text-sm font-semibold text-[#4E6F8A] transition hover:bg-[#EBF4FB]'
                      >
                        Remove
                      </button>
                    )}
                  </div>
                  );
                })}
              </div>
              <button type='button' onClick={addCitizenship} className='mt-3 text-sm font-semibold text-[#1E4E80]'>
                + Add another citizenship
              </button>
              {errors.citizenships && <p className={errorClass}>{errors.citizenships}</p>}
              {referenceCountriesError && (
                <p className={errorClass}>Could not load countries. Please refresh and try again.</p>
              )}
              </div>
            )}
          </fieldset>

          {upsertUserOnboardingProfileError && (
            <div className='mt-5 rounded-[12px] border border-[#F4C7C3] bg-[#FFF7F6] px-4 py-3 text-sm font-medium text-[#A83227]'>
              {upsertUserOnboardingProfileError?.message ??
                upsertUserOnboardingProfileError?.detail ??
                'Could not save your compliance declarations. Please try again.'}
            </div>
          )}

          {Object.keys(errors).length > 0 && hasSubmitted && (
            <div className='mt-5 rounded-[12px] border border-[#F4C7C3] bg-[#FFF7F6] px-4 py-3 text-sm font-medium text-[#A83227]'>
              Please complete the highlighted fields before continuing.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Compliance() {
  const { user } = useSelector((state) => state.user);
  if (!user) return null;
  if (user.userType === UserType.Company) return <CompanyRisk />;
  return <ComplianceIndividual />;
}

export default Compliance;
